import api from 'api';
import axios from 'axios';
import { Loading } from 'components/elements';
import { MainSection, MainSectionContainer } from 'components/general-styles';
import ProfileTabsBox from 'components/main/profile/ProfileTabsBox/ProfileTabsBox';
import EventNotes from 'components/main/profile/event-notes/EventNotes';
import StickyNotes from 'components/main/profile/sticky-notes/StickyNotes';
import SummaryBox from 'components/main/profile/summary-box/SummaryBox';
import Toast from 'components/toast';
import { NODE_REVIEW_LIST_NAMES } from 'config';
import { ReviewListSlugs } from 'config/reviewLists';
import { getClinicFilterFromSearch } from 'controllers/review/clinic-filter';
import { isReviewList4 } from 'controllers/review/review-controller';
import useLastProgram from 'hooks/useLastProgram';
import node_api from 'node-api';
import ClinicsServiceClient from 'node-api/clinics/clinics.client';
import { ReviewListClient } from 'node-api/reviewList/reviewlistClient';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { PatientContext } from 'state/contexts/patient';
import { ProviderContext } from 'state/contexts/provider';
import { ReviewLists4Context } from 'state/contexts/reviewLists4';
import { UserContext } from 'state/contexts/user';
import { ActionTypes as PatientActionTypes } from 'state/reducers/patient';
import { ActionTypes as ProviderActionTypes } from 'state/reducers/provider';
import { debug, getImpersonationParams } from 'utils/helpers';
import { DEFAULT_TIMEZONE } from 'utils/timezones';
import Chat from '../profile/chat/Chat';
import { getUserInfo } from '../rpm-event/util';
import ReviewList from './review-list/ReviewList';

type Props = RouteComponentProps<{ id: string }>;

const Review: React.FC<Props> = ({ match, location }) => {
  const { providerDispatch } = useContext(ProviderContext);
  const { patientState, patientDispatch } = useContext(PatientContext);
  const { userState } = useContext(UserContext);
  const { reviewLists4State } = useContext(ReviewLists4Context);
  const patient = patientState.current;

  const { id: reviewList } = match.params;

  const [reviewListLabel, setReviewListLabel] = useState<string>();
  const [loadingPatientsList, setLoadingPatientsList] = useState<boolean>(true);
  const [loadingPatientDetails, setLoadingPatientDetails] = useState<boolean>(true);
  const [user] = useState(getUserInfo(userState));

  const currentProgram = useLastProgram();

  useEffect(() => {
    if (!currentProgram) {
      return;
    }

    patientDispatch({ type: PatientActionTypes.SET_PROGRAM, payload: currentProgram });
  }, [currentProgram]);

  useEffect(() => {
    const patientsListSource = axios.CancelToken.source();

    const fetchPatientsList = async () => {
      try {
        let data;

        // Spread unconditionally: this effect has `[]` deps and fires the
        // moment Review mounts, which can be before `fetchUserData()` has
        // populated `userState`. Gating on `userState.isOriginalUser` here
        // dropped `original_user_uuid` whenever that race was lost, making core
        // resolve RBAC against the impersonated role. `getImpersonationParams()`
        // reads localStorage synchronously and returns {} when not impersonating.
        const params = { ...getImpersonationParams() };

        if (isReviewList4(reviewList as ReviewListSlugs, reviewLists4State.data)) {
          data = await new ReviewListClient().getReviewList4(
            { reviewListName: reviewList },
            patientsListSource.token
          );
        } else if (NODE_REVIEW_LIST_NAMES.includes(reviewList)) {
          // reviewlist3-sam — hits the intervened /node-be/v2/reviews/{slug}
          // endpoint via REACT_APP_API_NODE_BASE_URL. The bearer is sent in
          // the Authorization header by node_api(); the legacy /AUTH_TOKEN
          // URL suffix is no longer appended so the token does not leak
          // into access logs.
          const response = await node_api().get(`reviews/${reviewList}`, {
            cancelToken: patientsListSource.token,
            params,
          });

          data = response.data;
        } else {
          const response = await api().get(`reviews/${reviewList}/AUTH_TOKEN`, {
            cancelToken: patientsListSource.token,
            params,
          });

          data = response.data;
        }

        patientDispatch({
          type: PatientActionTypes.SET,
          payload: { patients: data.users, reviewedPatients: [] },
        });
        // Restore the clinic filter from the URL (reloads, shared links, remounts)
        patientDispatch({
          type: PatientActionTypes.SET_CLINIC_FILTER,
          payload: { clinicId: getClinicFilterFromSearch(location.search) },
        });

        setReviewListLabel(data.review_list || data.label);
        setLoadingPatientsList(false);
      } catch (err) {
        if (!axios.isCancel(err)) {
          debug(`${err}`);
          setLoadingPatientsList(false);

          Toast.show('error', `Couldn't load review list data, please try again later!`);
        }
      }
    };

    if (reviewList) {
      fetchPatientsList();
    }

    return () => {
      patientDispatch({ type: PatientActionTypes.CLEAR, payload: undefined });

      patientsListSource.cancel();
    };
  }, []);

  useEffect(() => {
    const currentPatient = patientState.current;

    if (loadingPatientsList || !currentPatient) {
      return;
    }

    const providersSource = axios.CancelToken.source();
    const videoLinkSource = axios.CancelToken.source();

    const fetchPatientDetails = async () => {
      try {
        // Get providers
        const { data: providers } = await api().get(
          `users/${currentPatient.id}/providers/AUTH_TOKEN`,
          {
            cancelToken: providersSource.token,
          }
        );

        providerDispatch({ type: ProviderActionTypes.SET, payload: { providers } });
      } catch (err) {
        if (!axios.isCancel(err)) {
          debug(`${err}`);
        }
      }

      const clinicsServiceClient = new ClinicsServiceClient();
      try {
        const clinic = await clinicsServiceClient.getClinic(currentPatient.clinic_id!);
        if (!clinic.timezone) {
          throw new Error(`Clinic ID=${clinic.id} does not have a timezone`);
        }

        currentPatient.timezone = clinic.timezone;
      } catch (err) {
        currentPatient.timezone = DEFAULT_TIMEZONE;
        debug(`${err}`);
      }
      setLoadingPatientDetails(false);
    };

    fetchPatientDetails();

    return () => {
      providersSource.cancel();
      videoLinkSource.cancel();
    };
  }, [patientState, loadingPatientsList]);

  useEffect(() => {
    if (!loadingPatientsList && !patientState.data.length) {
      // There are no members to review
      setLoadingPatientDetails(false);
    }
  }, [loadingPatientsList, patientState]);

  if (loadingPatientsList || loadingPatientDetails) {
    return <Loading />;
  }

  return (
    <MainSectionContainer>
      {patient ? (
        <>
          <MainSection style={{ minWidth: 800 }}>
            <ReviewList reviewListLabel={reviewListLabel} reviewListName={reviewList} />
            <StickyNotes key={`sticky-${patient.id}`} />
            <EventNotes key={`event-${patient.id}`} />
            <SummaryBox
              listLabel={reviewListLabel}
              listName={reviewList}
              key={`summary-${patient.id}`}
              hasReview
            />
            <ProfileTabsBox user={user} reviewList={reviewList} />
          </MainSection>

          <Chat reviewList={reviewListLabel} key={`chat-${patient.id}`} />
        </>
      ) : (
        <div style={{ marginTop: 15 }}>No members to review just yet</div>
      )}
    </MainSectionContainer>
  );
};

export default Review;
